import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calculator, Building2, Mail, Lock, User } from 'lucide-react';

const LoginPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    clientId: ''
  });

  // Register form state
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    password: '',
    clientId: ''
  });

  const from = location.state?.from?.pathname || '/';

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const success = await login(loginData.email, loginData.password, loginData.clientId);
    
    if (success) {
      navigate(from, { replace: true });
    }
    setIsLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const success = await register(
      registerData.name,
      registerData.email, 
      registerData.password, 
      registerData.clientId
    );
    
    if (success) {
      navigate(from, { replace: true });
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-equipr-black relative overflow-hidden">
        <div className="absolute inset-0 bg-equipr-black"></div>
        <div className="relative z-10 flex flex-col justify-center px-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded bg-equipr-yellow flex items-center justify-center">
              <Calculator className="w-7 h-7 text-equipr-black" />
            </div>
            <span className="font-heading font-bold text-3xl text-white tracking-tight">EQUIPR</span>
          </div>
          
          <h1 className="text-4xl font-heading font-bold text-white mb-4 leading-tight">
            EBA Payroll<br />Interpreter
          </h1>
          
          <p className="text-lg text-white/70 mb-8 max-w-md leading-relaxed">
            Automatically interpret Enterprise Bargaining Agreements, validate timesheets against submitted job information, and generate pay-ready payroll outputs.
          </p>

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded bg-equipr-yellow flex items-center justify-center mt-0.5">
                <span className="text-equipr-black text-xs font-bold">1</span>
              </div>
              <div>
                <p className="text-white font-medium">Upload EBA Documents</p>
                <p className="text-white/50 text-sm">PDF or DOCX format supported</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded bg-equipr-yellow flex items-center justify-center mt-0.5">
                <span className="text-equipr-black text-xs font-bold">2</span>
              </div>
              <div>
                <p className="text-white font-medium">AI-Powered Interpretation</p>
                <p className="text-white/50 text-sm">Extract pay rates and conditions automatically</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded bg-equipr-yellow flex items-center justify-center mt-0.5">
                <span className="text-equipr-black text-xs font-bold">3</span>
              </div>
              <div>
                <p className="text-white font-medium">Validate Timesheet Data</p>
                <p className="text-white/50 text-sm">Cross-check against job data for accuracy</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded bg-equipr-yellow flex items-center justify-center mt-0.5">
                <span className="text-equipr-black text-xs font-bold">4</span>
              </div>
              <div>
                <p className="text-white font-medium">Generate Payroll</p>
                <p className="text-white/50 text-sm">Export to Excel or Xero-ready CSV</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <div className="w-10 h-10 rounded bg-equipr-yellow flex items-center justify-center">
              <Calculator className="w-6 h-6 text-equipr-black" />
            </div>
            <span className="font-heading font-bold text-2xl text-equipr-black tracking-tight">EQUIPR</span>
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-heading font-bold text-equipr-black">Welcome</CardTitle>
              <CardDescription className="text-slate-500">
                Sign in to access your payroll dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="login" className="w-full">
                {/* Registration temporarily disabled - contact admin to create accounts */}
                <TabsList className="grid w-full grid-cols-1 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                  {/* <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger> */}
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-client" className="text-slate-700">Client ID</Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="login-client"
                          placeholder="Your organization ID"
                          value={loginData.clientId}
                          onChange={(e) => setLoginData({ ...loginData, clientId: e.target.value })}
                          className="pl-10 bg-slate-50 border-slate-200 focus:bg-white"
                          required
                          data-testid="login-client-input"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="login-email" className="text-slate-700">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="name@company.com"
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          className="pl-10 bg-slate-50 border-slate-200 focus:bg-white"
                          required
                          data-testid="login-email-input"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="login-password" className="text-slate-700">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="login-password"
                          type="password"
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          className="pl-10 bg-slate-50 border-slate-200 focus:bg-white"
                          required
                          data-testid="login-password-input"
                        />
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-equipr-black hover:bg-equipr-black/90 text-white" 
                      disabled={isLoading}
                      data-testid="login-submit-btn"
                    >
                      {isLoading ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-client" className="text-slate-700">Client ID</Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="register-client"
                          placeholder="Your organization ID"
                          value={registerData.clientId}
                          onChange={(e) => setRegisterData({ ...registerData, clientId: e.target.value })}
                          className="pl-10 bg-slate-50 border-slate-200 focus:bg-white"
                          required
                          data-testid="register-client-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="register-name" className="text-slate-700">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="register-name"
                          placeholder="John Smith"
                          value={registerData.name}
                          onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                          className="pl-10 bg-slate-50 border-slate-200 focus:bg-white"
                          required
                          data-testid="register-name-input"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-email" className="text-slate-700">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="name@company.com"
                          value={registerData.email}
                          onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                          className="pl-10 bg-slate-50 border-slate-200 focus:bg-white"
                          required
                          data-testid="register-email-input"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-password" className="text-slate-700">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="register-password"
                          type="password"
                          placeholder="••••••••"
                          value={registerData.password}
                          onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                          className="pl-10 bg-slate-50 border-slate-200 focus:bg-white"
                          required
                          data-testid="register-password-input"
                        />
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-equipr-black hover:bg-equipr-black/90 text-white" 
                      disabled={isLoading}
                      data-testid="register-submit-btn"
                    >
                      {isLoading ? 'Creating account...' : 'Create Account'}
                    </Button>
                  </form>
                </TabsContent>
                {/* End of disabled registration */}
              </Tabs>
              
              {/* Contact info for new accounts */}
              <p className="text-center text-xs text-slate-500 mt-4 pt-4 border-t">
                Need an account? Contact your administrator.
              </p>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-slate-400 mt-6">
            By continuing, you agree to our Terms of Service and Privacy Policy.
          </p>
          
          {/* Version Display */}
          <p className="text-center text-xs text-slate-400 mt-4" data-testid="app-version">
            {process.env.REACT_APP_VERSION || 'Development'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
